import { useMemo } from 'react';
import { useServiceAgreementChargeTypes } from '@api/enterprise/serviceAgreements';
import type { ServiceAgreementChargeType } from '../types/serviceAgreementChargeType.types';

export const useServiceAgreementChargeTypesData = (enabled = true) => {
  const { data, isLoading, error } = useServiceAgreementChargeTypes(
    {
      orderBy: 'group asc, name asc'
    },
    enabled
  );

  const chargeTypes = useMemo(() => {
    // The API returns an array directly, not wrapped in { value: [...] }
    if (!data) return [];
    const dataArray = Array.isArray(data) ? data : data.value || [];
    return dataArray.map(
      (item): ServiceAgreementChargeType => ({
        branch: item.branch,
        group: item.group,
        name: item.name,
        type: item.type,
        charge: item.charge,
        description: item.description,
        unitOfMeasure: item.unitOfMeasure,
        isDefault: item.isDefault
      })
    );
  }, [data]);

  const groups = useMemo(() => {
    const uniqueGroups = new Set<string>();
    chargeTypes.forEach(ct => {
      if (ct.group) {
        uniqueGroups.add(ct.group);
      }
    });
    return Array.from(uniqueGroups).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [chargeTypes]);

  const getTypesByGroup = (group: string) => {
    return chargeTypes
      .filter(ct => ct.group === group)
      .sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      });
  };

  return {
    chargeTypes,
    groups,
    getTypesByGroup,
    isLoading,
    error
  };
};
